<template>
  <div>
    <slot name="label" :failed="failed">
      <p
        v-if="label"
        class="text-base font-bold"
        :class="{
          'text-error': failed,
        }"
      >
        {{ label }}
      </p>
    </slot>
    <div class="flex flex-wrap w-full relative">
      <JoszakiInputWrapper
        :class="{ '!bg-gray-100 dark:!bg-gray-600': _disabled }"
      >
        <div
          class="text-gray-300 placeholder-gray-300 h-full cursor-pointer flex items-center gap-1 flex-initial dark:text-gray-500"
          :class="{ '!bg-gray-100 dark:!bg-gray-600': _disabled }"
          @click.stop="downClicked"
        >
          <template v-if="value.countryCode">
            <div class="flex-0">
              <IconComponent
                pack="flags"
                :icon="value.countryCode.iso.trim()"
                class="w-6"
              />
            </div>
            <span class="text-gray-700 dark:text-gray-200">
              +{{ value.countryCode.code }}
            </span>
          </template>
          <template v-else>
            <span />
            <span />
          </template>
          <div v-if="phoneCountryCodes.length > 1" class="flex-0">
            <IconComponent icon="angle-down" class="text-gray-700" />
          </div>
        </div>

        <input
          ref="input"
          v-model="state.internalValue"
          type="tel"
          class="flex-1 focus:outline-none min-w-0 dark:bg-transparent dark:text-gray-200"
          :class="{ '!bg-gray-100 dark:!bg-gray-600': _disabled }"
          :placeholder="placeholder"
          v-bind="$attrs"
          :disabled="_disabled"
          @keypress="numericOnly"
          @input="onInput"
          @keyup="$emit('keyup', $event)"
        />

        <div class="flex-0 flex items-center cursor-pointer text-gray-400">
          <IconComponent
            :class="{
              'text-success': !invalid,
              'text-error': invalid,
            }"
            :icon="invalid ? 'times-circle' : 'check-circle'"
          />
        </div>
      </JoszakiInputWrapper>

      <div
        v-show="state.showDropdown"
        class="absolute bg-white rounded-md z-10 border border-gray-300 shadow-md max-w-[6rem] dark:bg-gray-800 dark:border-gray-600"
        :class="{
          'top-full left-0 right-0': dropDownPosition === 'bottom',
          'bottom-full left-0 right-0': dropDownPosition === 'top',
        }"
      >
        <div ref="container" class="max-h-52 overflow-y-auto">
          <div
            v-for="countryCode in phoneCountryCodes"
            :key="countryCode.id"
            class="py-2 pl-2 font-semibold flex gap-2 items-center px-1 hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-700"
            @click="selectCountryCode(countryCode)"
          >
            <IconComponent
              pack="flags"
              :icon="countryCode.iso.trim()"
              class="w-6"
            />
            +{{ countryCode.code }}
          </div>
        </div>
      </div>
    </div>
    <p
      v-if="showErrorMsg"
      class="text-sm text-error"
      :class="{
        invisible: !failed,
      }"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import {
  computed,
  reactive,
  useContext,
  inject,
} from "@nuxtjs/composition-api";
import {
  formatNumber,
  isValidPhoneNumber,
  parseNumber,
} from "libphonenumber-js";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import JoszakiInputWrapper from "./input/JoszakiInputWrapper.vue";
import { getCountryCode } from "~/locales/config.js";

export default {
  components: { JoszakiInputWrapper },
  props: {
    value: {
      type: Object,
      required: true,
    },
    placeholder: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "search",
    },
    iconPack: {
      type: String,
      default: "fas",
    },
    validationState: {
      type: Object,
      required: false,
      default: () => {},
    },
    label: {
      type: String,
      default: null,
    },
    showErrorMsg: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dropDownPosition: {
      type: String,
      default: "bottom",
      validator: (value) => {
        return ["top", "bottom"].includes(value);
      },
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    mobileOnly: {
      type: Boolean,
      default: false,
    },
    professionalOnly: {
      type: Boolean,
      default: false,
    },
    customerOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    try {
      const {
        $validation: { rules },
        i18n,
        store,
      } = useContext();
      const { disabled: formDisabled } = inject("form", { disabled: false });

      const phoneCountryCodes = store.getters["data/phoneCountryCodes"]
        .filter((pcc) => !props.professionalOnly || pcc.enabledForProfessional)
        .filter((pcc) => !props.customerOnly || pcc.enabledForCustomer);

      const state = reactive({
        internalValue: "",
        showDropdown: false,
      });

      function isPhoneValid(value) {
        if (!props.value.countryCode) {
          // No country code yet during init; the default is assigned in
          // created(). Expected transient state, so fail validation quietly.
          return false;
        }

        const regexp = props.mobileOnly
          ? props.value.countryCode.mobileRegexp
          : props.value.countryCode.regexp;

        return (
          isValidPhoneNumber(
            value,
            props.value.countryCode.iso.trim().toUpperCase()
          ) && new RegExp(regexp).test(value.replaceAll(" ", ""))
        );
      }

      const phoneValid = computed(() => {
        return isPhoneValid(state.internalValue);
      });

      const validations = computed(() => {
        const valueErrorMessage = props.mobileOnly
          ? i18n.t("validations.phoneNumberMobile")
          : i18n.t("validations.phoneNumber");
        return {
          value: {
            number: {
              phone: helpers.withMessage(valueErrorMessage, (value) =>
                isPhoneValid(value)
              ),
            },
            countryCode: {
              required: rules.required,
              validCountryCode: helpers.withMessage(
                i18n.t("validations.phoneNumberCountry"),
                (value) =>
                  phoneCountryCodes.find((pcc) => pcc.iso === value.iso)
              ),
            },
          },
        };
      });

      const formData = computed(() => ({
        value: props.value,
      }));

      const v$ = useVuelidate(validations.value, formData);

      return {
        v$,
        state,
        phoneValid,
        phoneCountryCodes,
        formDisabled,
      };
    } catch (error) {
      console.error("PhoneInput setup error", error);
    }
  },
  computed: {
    _disabled() {
      return this.disabled || this.formDisabled;
    },
    errorMessage() {
      return (
        this.validationState?.$errors?.[0]?.$message ||
        this.v$?.$errors?.[0]?.$message ||
        "placeholder so height is correctly computed"
      );
    },
    failed() {
      return this.validationState?.$error || this.v$.$error;
    },
    invalid() {
      return this.validationState?.$invalid || this.v$.$invalid;
    },
  },
  watch: {
    value: {
      handler(newValue) {
        this.state.internalValue = this.convertToInternational(newValue.number);
      },
      immediate: true,
    },
  },
  created() {
    const fallbackCountryCode = getCountryCode(this.$i18n.locale);
    const code = this.phoneCountryCodes.find(
      (countryCode) =>
        countryCode.iso.trim() ===
        (this.value?.countryCode?.iso ?? fallbackCountryCode)
    );
    if (code) {
      this.selectCountryCode(code);
    }
  },
  mounted() {
    // Parents gating a "Next" button can't reactively read `invalid` via
    // $refs, so push validity changes (incl. the libphonenumber format check)
    // up as an event. Immediate, so prefilled values gate correctly on mount.
    // Client-only: on the server computeds aren't cached, so this watcher
    // re-triggers itself through vuelidate forever and wedges the SSR process.
    this.$watch(
      "invalid",
      (invalid) => {
        this.$emit("validity-change", !invalid);
      },
      { immediate: true }
    );
    this.state.internalValue = this.convertToInternational(this.value.number);
    document.addEventListener("click", this.outsideClickHandler);
    if (this.autofocus) {
      this.focus();
    }
  },
  destroyed() {
    document.removeEventListener("click", this.outsideClickHandler);
  },
  methods: {
    onInput() {
      this.$emit("input", {
        ...this.value,
        number: this.state.internalValue.replaceAll(" ", ""),
      });
    },
    numericOnly(e) {
      this.$emit("keypress", e);
      if (e.key === "Backspace" || e.key === "Delete") {
        return true;
      }

      const value = e.target.value.replaceAll(" ", "");
      if (
        value.length === this.value.countryCode.maxLength &&
        // if text is selected, allow to overwrite it, otherwise prevent typing
        !(e.target.selectionEnd - e.target.selectionStart)
      ) {
        e.preventDefault();
        return;
      }

      if (e.key === " ") {
        return true;
      }

      if (isNaN(e.key)) {
        e.preventDefault();
        return false;
      }
    },
    convertToInternational(value) {
      if (!value) {
        return "";
      }
      try {
        const parsedNumber = parseNumber(
          value,
          this.value.countryCode?.iso.trim().toUpperCase()
        );
        const formatted = formatNumber(parsedNumber, "INTERNATIONAL");
        if (formatted.length < 3) {
          return value;
        }
        const cutOff = `${this.value.countryCode?.code}`.length + 1;
        return formatted.slice(cutOff).trim();
      } catch (error) {
        console.error("convertToInternational error", error);
        return value;
      }
    },
    selectCountryCode(countryCode) {
      this.state.showDropdown = false;
      this.$emit("input", {
        ...this.value,
        countryCode,
      });
    },
    downClicked() {
      if (this._disabled) {
        return;
      }
      if (this.phoneCountryCodes.length < 2) {
        return;
      }
      this.state.showDropdown = !this.state.showDropdown;
    },
    outsideClickHandler(e) {
      if (!this.$el.contains(e.target) && this.state.showDropdown) {
        this.state.showDropdown = false;
      }
    },
    focus() {
      this.$refs.input.focus();
    },
  },
};
</script>
